import * as Apollo from '@apollo/client'
import { parse } from 'cookie'
import { NextPageContext } from 'next'
import { NextRouter } from 'next/dist/shared/lib/router/router'
import { useRouter } from 'next/router'
import { useMemo } from 'react'

import { ParsedUrlQuery } from 'querystring'

import { AUTH_DATA_KEYS } from './constants'

import { isBoolean } from '~lib/helpers'

enum ROUTES {
  HOME = '/',
  AUTH = '/auth',
  AUTH_RELEASE_NOTES = '/auth/release-notes',
  LAUNCHES = '/[organizationSlug]/launches',
  LAUNCH = '/[organizationSlug]/launches/[launchId]',
  MOBILE = '/mobile',
  TASKS = '/[organizationSlug]/tasks',
  TASK = '/[organizationSlug]/task',
  OUTSIDE = '/[organizationSlug]',
  THANKYOU = '/thank-you',
  ERROR_404 = '/404',
  PUBLIC = '/public',
}

export const PUBLIC = 'public' as const
export const LAUNCHES = 'launches' as const
export const ASSETS = 'assets' as const
export const BRAND = 'brand' as const

export enum LAUNCH_PAGES {
  MISSION_CONTROL = 'mission-control',
  MISSION_CONTROL_V2 = 'mission-control-v2',
  COMPETITIVE_INTELLIGENCE = 'competitive-intelligence',
  COPY_AND_ASSETS = 'copy-and-assets',
  RESEARCH_HUB = 'research-hub',
  RELEASE_NOTES = 'release-notes',
  GTM_TIMELINE = 'gtm-timeline',
  CALENDAR = 'calendar',
  MEASUREMENTS = 'measurements',
  TASKS = 'tasks',
  PRODUCT_ROADMAP = 'product-roadmap',
  OKRS = 'objectives',
}

export const LAUNCH_PATHNAMES = {
  LAUNCH: ROUTES.LAUNCH as const,
  MISSION_CONTROL: `${ROUTES.LAUNCH}/${LAUNCH_PAGES.MISSION_CONTROL}` as const,
  MISSION_CONTROL_V2: `${ROUTES.LAUNCH}/${LAUNCH_PAGES.MISSION_CONTROL_V2}` as const,
  GTM_TIMELINE: `${ROUTES.LAUNCH}/${LAUNCH_PAGES.GTM_TIMELINE}` as const,
  CALENDAR: `${ROUTES.LAUNCH}/${LAUNCH_PAGES.CALENDAR}` as const,
  COMPETITIVE_INTELLIGENCE: `${ROUTES.LAUNCH}/${LAUNCH_PAGES.COMPETITIVE_INTELLIGENCE}` as const,
  COPY_AND_ASSETS: `${ROUTES.LAUNCH}/${LAUNCH_PAGES.COPY_AND_ASSETS}` as const,
  MEASUREMENTS: `${ROUTES.LAUNCH}/${LAUNCH_PAGES.MEASUREMENTS}` as const,
  RELEASE_NOTES: `${ROUTES.LAUNCH}/${LAUNCH_PAGES.RELEASE_NOTES}` as const,
  RESEARCH_HUB: `${ROUTES.LAUNCH}/${LAUNCH_PAGES.RESEARCH_HUB}` as const,
  TASKS: `${ROUTES.LAUNCH}/${LAUNCH_PAGES.TASKS}` as const,
}

export const LAUNCH_PATHNAMES_PUBLIC = {
  LAUNCH: `${LAUNCH_PATHNAMES.LAUNCH}/${PUBLIC}` as const,
  MISSION_CONTROL: `${LAUNCH_PATHNAMES.MISSION_CONTROL}/${PUBLIC}` as const,
  GTM_TIMELINE: `${LAUNCH_PATHNAMES.GTM_TIMELINE}/${PUBLIC}` as const,
  COMPETITIVE_INTELLIGENCE: `${LAUNCH_PATHNAMES.COMPETITIVE_INTELLIGENCE}/${PUBLIC}` as const,
  COPY_AND_ASSETS: `${LAUNCH_PATHNAMES.COPY_AND_ASSETS}/${PUBLIC}` as const,
  MEASUREMENTS: `${LAUNCH_PATHNAMES.MEASUREMENTS}/${PUBLIC}` as const,
  RELEASE_NOTES: `${LAUNCH_PATHNAMES.RELEASE_NOTES}/${PUBLIC}` as const,
  RESEARCH_HUB: `${LAUNCH_PATHNAMES.RESEARCH_HUB}/${PUBLIC}` as const,
  TASKS: `${LAUNCH_PATHNAMES.TASKS}/${PUBLIC}` as const,
}

export enum OUTSIDE_PAGES {
  TASKS = 'tasks',
}

export const OUTSIDE_PATHNAMES = {
  OUTSIDE: ROUTES.OUTSIDE as const,
  TASKS: `${ROUTES.OUTSIDE}/${OUTSIDE_PAGES.TASKS}` as const,
}

export default ROUTES

export const createParams = (params = {}) => {
  return new URLSearchParams(params).toString()
}
/** @deprecated Use OrganizatioNavigation or SessingsNavigation from ~shared/navigation instead */
export const createUrl = (path: string[] | keyof typeof ROUTES, query?: Record<string, any>) => {
  const params = createParams(query)
  const pathname = Array.isArray(path) ? `/${path.filter(Boolean).join('/')}` : ROUTES[path]
  const url = params ? `${pathname}?${params}` : `${pathname}`
  return url
}

// Redirects

export const isAuth = (pathname: string) => {
  return pathname.includes(ROUTES.AUTH) || pathname.includes(ROUTES.AUTH_RELEASE_NOTES)
}

export const isThankyou = (pathname: string) => {
  return pathname.includes(ROUTES.THANKYOU)
}

/**
 * Check whether user is authorized
 */
export const isAuthorized = (cookie: Record<string, any> = {}) => {
  // TODO - fix tech debt
  return AUTH_DATA_KEYS.ORGANIZATION_SLUG in cookie && AUTH_DATA_KEYS.USER_ID in cookie
}

/**
 * Check whether wrong organization in the url
 */
export const isWrongOrganization = ({
  url,
  pathname,
  cookie = {},
  query = {},
}: {
  url: string
  pathname: string
  cookie: Record<string, any>
  query: Record<string, any>
}) => {
  return (
    pathname.includes('[organizationSlug]') &&
    cookie.organizationSlug &&
    (!url.includes(encodeURIComponent(cookie.organizationSlug)) || cookie.organizationSlug !== query.organizationSlug)
  )
}

/**
 * Redirects to the same url with correct organization slug in the url
 */
export const redirectOrganization = ({ res, req, query }: NextPageContext, cookie: Record<string, any>) => {
  const url = req.url.replace(
    encodeURIComponent(query.organizationSlug as string),
    encodeURIComponent(cookie.organizationSlug)
  )

  res.writeHead(302, { Location: url }).end()
  return {}
}

/**
 * Redirect unauthorized user to auth page
 */
export const redirectUnauthorized = ({ res }: NextPageContext) => {
  const url = `${ROUTES.AUTH}`
  res.writeHead(302, { Location: url }).end()

  return {}
}

/**
 * Redirect authorized user to home page
 */
export const redirectAuthorized = ({ res }: NextPageContext, cookie: Record<string, any>) => {
  const organizationSlug = cookie[AUTH_DATA_KEYS.ORGANIZATION_SLUG]

  if (organizationSlug && !cookie[AUTH_DATA_KEYS.IS_NEW_USER]) {
    const Location = createUrl([organizationSlug, 'launches'])
    res.writeHead(302, { Location }).end()
  }
  return {}
}

export const redirectToMobile = ({ req, res }: NextPageContext) => {
  if (!req.url.startsWith(ROUTES.MOBILE)) {
    res.writeHead(302, { Location: ROUTES.MOBILE }).end()
  }
  return {}
}

export const redirectTo404 = ({ res }: NextPageContext) => {
  const Location = createUrl(['404'])

  res.writeHead(302, { Location }).end()
  return {}
}

export const createLaunchUrl = (launchId: string) => {
  const cookies = parse(document.cookie)
  const organizationSlug = cookies[AUTH_DATA_KEYS.ORGANIZATION_SLUG]

  if (!organizationSlug) {
    throw new Error('Organization slug is empty')
  }

  return createUrl([organizationSlug, LAUNCHES, launchId])
}

export function isGrowthToolPage(pathname: string) {
  return pathname.startsWith('/public/growth-tool')
}

export function parseQueryParam<T extends string>(query: ParsedUrlQuery, key: string, defaultParam?: T) {
  const param = Array.isArray(query[key]) ? (query[key][0] as T) : (query[key] as T)
  return param || defaultParam
}

export class Navigation {
  static isPublicRoute(router: NextRouter) {
    return router.route.startsWith('/public')
  }

  static createPrivateOrganizationUrl(router: NextRouter, path: string[]) {
    const organizationSlug = parseQueryParam(router.query, 'organizationSlug')
    if (!organizationSlug) throw new Error('Organization slug is empty')

    return createUrl([organizationSlug, ...path])
  }

  static createPublicOrganizationUrl(router: NextRouter, path: string[]) {
    const organizationSlug = parseQueryParam(router.query, 'organizationSlug')
    if (!organizationSlug) throw new Error('Organization slug is empty')

    return createUrl(['public', organizationSlug, ...path])
  }

  static createRoadmapItemUrl(router: NextRouter, options: { roadmapItemId: string; public?: boolean }) {
    const isPublic = isBoolean(options.public) ? options.public : Navigation.isPublicRoute(router)

    if (isPublic) {
      return Navigation.createPublicOrganizationUrl(router, ['roadmap', 'items', options.roadmapItemId])
    }

    return Navigation.createPrivateOrganizationUrl(router, ['roadmap', options.roadmapItemId])
  }

  static createRoadmapUrl(router: NextRouter, options: { public?: boolean; roadmapId?: string } = {}) {
    const isPublic = isBoolean(options.public) ? options.public : Navigation.isPublicRoute(router)

    if (isPublic) {
      return Navigation.createPublicOrganizationUrl(router, ['roadmap'])
    }

    return Navigation.createPrivateOrganizationUrl(router, ['roadmap'])
  }

  static createCalendarUrl(router: NextRouter, options: { public?: boolean } = {}) {
    const isPublic = isBoolean(options.public) ? options.public : Navigation.isPublicRoute(router)

    return isPublic
      ? Navigation.createPublicOrganizationUrl(router, ['calendar'])
      : Navigation.createPrivateOrganizationUrl(router, ['calendar'])
  }

  static createTimelineUrl(router: NextRouter, options: { public?: boolean } = {}) {
    const isPublic = isBoolean(options.public) ? options.public : Navigation.isPublicRoute(router)

    return isPublic
      ? Navigation.createPublicOrganizationUrl(router, ['timeline'])
      : Navigation.createPrivateOrganizationUrl(router, ['timeline'])
  }

  static createLaunchUrl(
    router: NextRouter,
    options: { launchId: string; page?: LAUNCH_PAGES | 'launch-brief'; public?: boolean }
  ) {
    const isPublic = isBoolean(options.public) ? options.public : Navigation.isPublicRoute(router)

    // TODO move public launch pages to /public
    const organizationSlug = parseQueryParam(router.query, 'organizationSlug')
    if (!organizationSlug) throw new Error('Organization slug is empty')

    if (isPublic) {
      // TODO move launch brief from index to /launch-brief
      if (options.page === 'launch-brief') {
        return createUrl([organizationSlug, 'launches', options.launchId, 'public'])
      }

      const page =
        options.page &&
        [
          LAUNCH_PAGES.COPY_AND_ASSETS,
          LAUNCH_PAGES.GTM_TIMELINE,
          LAUNCH_PAGES.MISSION_CONTROL_V2,
          LAUNCH_PAGES.TASKS,
        ].includes(options.page as LAUNCH_PAGES)
          ? options.page
          : LAUNCH_PAGES.MISSION_CONTROL_V2

      return createUrl([organizationSlug, 'launches', options.launchId, page, 'public'])
    }

    // TODO move launch brief from index to /launch-brief
    if (options.page === 'launch-brief') {
      return createUrl([organizationSlug, 'launches', 'launches', options.launchId])
    }

    return createUrl([organizationSlug, 'launches', options.launchId, options.page || LAUNCH_PAGES.MISSION_CONTROL_V2])
  }
}

// TEMP helper to call public or private query based on current route
type OptionsType<Q, V> = Omit<Apollo.QueryHookOptions<Q, V>, 'onCompleted'>
type ResType<Q, V> = Omit<Apollo.QueryResult<Q, V>, 'subscribeToMore' | 'previousData' | 'updateQuery' | 'refetch'>

export function generatePublicOrPrivateQueryHook<Query, PublicQuery, Variables>(
  usePrivateQuery: (baseOptions?: Apollo.QueryHookOptions<Query, Variables>) => Apollo.QueryResult<Query, Variables>,
  usePublicQuery: (
    baseOptions?: Apollo.QueryHookOptions<PublicQuery, Variables>
  ) => Apollo.QueryResult<PublicQuery, Variables>,
  prefix = 'public'
) {
  return (baseOptions: OptionsType<Query, Variables>): ResType<Query, Variables> => {
    const router = useRouter()

    const isPublicPage = Navigation.isPublicRoute(router)

    const privateQueryRes = usePrivateQuery({
      ...baseOptions,
      skip: isPublicPage || baseOptions?.skip,
    })

    const publicQueryRes = usePublicQuery({
      ...baseOptions,
      skip: !isPublicPage || baseOptions?.skip,
    })

    return useMemo(() => {
      if (!isPublicPage) {
        return privateQueryRes
      }

      return {
        ...publicQueryRes,
        data: publicQueryRes.data
          ? Object.entries(publicQueryRes.data).reduce((acc, [key, value]) => {
              if (key.startsWith(prefix)) {
                const keyWithoutPublic = key.replace(prefix, '')
                const newKey = keyWithoutPublic.charAt(0).toLowerCase() + keyWithoutPublic.slice(1)
                acc[newKey] = value
                return acc
              }

              acc[key] = value
              return acc
            }, {} as Query)
          : undefined,
      }
    }, [isPublicPage, privateQueryRes, publicQueryRes])
  }
}
